import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { findWidget, findWidgetPlan } from '@entities/widget-card/lib';
import { useMetricTracker } from '@entities/widget-card/model';
import { IWidgetCard, IWidgetCategoriesList } from '@entities/widget-card/model/types';
import {
    useGetWidgetInfo,
    useGetPaymentWidgetDataQuery,
    useGetWidgetsListQuery,
    usePayWidgetByCardQuery,
    useEnableWidgetTrialPeriod,
} from '@entities/widget-shop/api/hooks';
import {
    IWidgetPaymentData,
    TWidgetPlan,
    TPaymentMethod,
    TPaymentMethodType,
} from '@entities/widget-shop/model/types';
import { submitPaymentForm } from '@shared/helpers';

import { getCurrentPaymentPeriodInfo } from '../lib';

export const usePaymentWidgetModal = ({
    currentWidgetId,
    isModalOpen,
    fromWidgetPage,
}: {
    currentWidgetId: IWidgetCard['id'] | null;
    isModalOpen: boolean;
    fromWidgetPage: boolean;
}) => {
    const { t } = useTranslation();
    const { trackers } = useMetricTracker();

    const { data: paymentDataFromList, isPending: isPaymentDataPending } =
        useGetPaymentWidgetDataQuery({
            widgetId: currentWidgetId,
            enabled: fromWidgetPage && isModalOpen,
        });
    const { data: widgetInfo, isPending: isWidgetInfoPending } = useGetWidgetInfo({
        enabled: !fromWidgetPage && isModalOpen && !!currentWidgetId,
        pickedWidgetId: currentWidgetId,
    });
    const { data: widgetsData } = useGetWidgetsListQuery({
        enabled: fromWidgetPage && isModalOpen,
    });

    const enableWidgetTrialPeriodMutation = useEnableWidgetTrialPeriod();

    const paymentData: IWidgetPaymentData['paymentData'] | undefined = fromWidgetPage
        ? paymentDataFromList
        : widgetInfo?.plans;
    const paymentContext: IWidgetCategoriesList['context'] | undefined = fromWidgetPage
        ? widgetsData?.context
        : widgetInfo?.context;
    const isPending = fromWidgetPage ? isPaymentDataPending : isWidgetInfoPending;

    const isTrialAvailable = fromWidgetPage
        ? findWidget({
              widgetId: currentWidgetId,
              data: widgetsData?.categories,
          })?.isTrialAvailable
        : widgetInfo?.widget?.isTrialAvailable;
    const trialDays = fromWidgetPage
        ? findWidget({
              widgetId: currentWidgetId,
              data: widgetsData?.categories,
          })?.trialDays
        : widgetInfo?.widget?.trialDays;

    const [activePeriodPicked, setActivePeriodPicked] = useState<TWidgetPlan['id'] | null>(null);
    const [activePaymentMethodType, setActivePaymentMethodType] = useState<
        TPaymentMethod['type'] | null
    >(null);

    const [autoRenewal, setAutoRenewal] = useState(true);
    const payWidgetMutation = usePayWidgetByCardQuery();

    const notMyCompany = paymentContext?.notMyCompany ?? false;

    useEffect(() => {
        if (!paymentData || !currentWidgetId) return;

        setActivePeriodPicked(paymentData.widgetPlans[0]?.id ?? null);
        setActivePaymentMethodType(paymentData.paymentMethods[0]?.type ?? null);
        setAutoRenewal(true);
    }, [paymentData, currentWidgetId]);

    const toggleAutoRenewal = () => {
        setAutoRenewal((prev) => !prev);
    };
    const onPeriodPick = (id: TWidgetPlan['id']) => {
        setActivePeriodPicked(id);
        setActivePaymentMethodType('card');
    };
    const onMethodPick = (type: TPaymentMethod['type']) => {
        setActivePaymentMethodType(type);
        if (type === 'ym') {
            setAutoRenewal(false);
        }
        if (type === 'card') {
            setAutoRenewal(true);
        }
    };

    const paymentPeriodInfo = getCurrentPaymentPeriodInfo({
        widgetPlans: paymentData?.widgetPlans,
        activePeriodPicked,
    });

    const trackPayment = () => {
        const foundWidgetPlan = findWidgetPlan({
            widgetPlanId: activePeriodPicked,
            widgetPlans: paymentData?.widgetPlans,
        });
        const foundWidget = findWidget({
            widgetId: currentWidgetId,
            data: widgetsData?.categories,
        });
        trackers.purchase({
            widget: fromWidgetPage ? foundWidget : widgetInfo.widget,
            paymentType: activePaymentMethodType,
            widgetPlan: foundWidgetPlan,
            isRecurrent: autoRenewal,
        });
    };

    const payByCardHandler = async () => {
        const res = await payWidgetMutation.mutateAsync({
            id: Number(activePeriodPicked),
            isRecurrent: autoRenewal,
            notMyCompany,
            paymentMethodType: activePaymentMethodType as Exclude<TPaymentMethodType, 'invoice'>,
        });

        if (res?.data?.success && res?.data?.paymentUrl && res?.data?.form) {
            submitPaymentForm({
                paymentUrl: res?.data?.paymentUrl,
                form: res?.data?.form,
            });
        } else if (res?.data?.success && res?.data?.paymentUrl) {
            window.location.href = res.data.paymentUrl;
        } else {
            console.error('Incorrect answer of payment API', res);
        }
    };

    const getAvailablePaymentMethods = () =>
        paymentData?.paymentMethods
            ?.map((method) => {
                const pickedPlan = paymentData?.widgetPlans?.find(
                    (plan) => plan.id === activePeriodPicked
                );
                if (pickedPlan?.availablePaymentMethods?.includes(method.type)) return method;
                return null;
            })
            .filter((item) => item != null);
    const availablePaymentMethods = getAvailablePaymentMethods();

    const onEnableWidgetTrialPeriod = async () => {
        await enableWidgetTrialPeriodMutation.mutateAsync({
            widgetId: currentWidgetId,
        });
    };

    return {
        paymentData,
        paymentContext,
        availablePaymentMethods,
        isPending,
        isPayByCardPending: payWidgetMutation.isPending,
        isTrialPending: enableWidgetTrialPeriodMutation.isPending,
        trialInfo: {
            isTrialAvailable,
            trialDays,
        },
        activePeriodPicked,
        activePaymentMethodType,
        autoRenewal,
        paymentPeriodInfo,
        payByCardHandler,
        toggleAutoRenewal,
        onPeriodPick,
        onMethodPick,
        onEnableWidgetTrialPeriod,
        trackPayment,
        t,
    };
};
